// ═══════════════════════════════════════════════════════════════════════════
// Centralised auth timing constants — Phase 2 polish.
//
// Before this file, every auth flow defined its own delays inline:
//   - AuthModal     : MAX_LOGIN_ATTEMPTS (5), LOCKOUT_DURATION_MS (60000)
//   - pinService    : LOCKOUT_DURATION (900000)
//   - EmailVerifyPg : RESEND_COOLDOWN_SECONDS (60), POLL_INTERVAL_MS (15000)
//   - PwResetPage   : 3000ms recovery-session timeout
//
// Spreading these across files makes tuning hard (you have to chase three
// branches to bump the lockout from 1 min to 5 min) and creates drift
// between mirroring features. Single source of truth here, with comments
// explaining the policy choice and any server-side counterpart.
//
// Naming convention: `*_MS` for milliseconds (used directly with timers),
// `*_SECONDS` for human-facing values (UI countdowns).
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Login form lockout — after N consecutive failed attempts the form goes
 * dark for `LOGIN_LOCKOUT_MS`. 5 attempts × 60 s gives a malicious script
 * about 60 attempts/hour, well under any sensible password's brute-force
 * resistance window. Mirrors Supabase's own server-side rate limiter so
 * users don't see "too many requests" coming from two layers at once.
 */
export const LOGIN_MAX_ATTEMPTS = 5;
export const LOGIN_LOCKOUT_MS = 60_000;

/**
 * PIN unlock lockout — much longer than the login lockout because PIN
 * unlock happens locally and a real attacker has the device. 15 minutes
 * matches iOS/Android conventions for similar timeouts.
 */
export const PIN_LOCKOUT_MS = 15 * 60_000;

/**
 * Email verification — how often to poll Supabase for `email_confirmed_at`
 * when no auth event fires (cross-device verification path). The default
 * push detection via `onAuthStateChange` covers same-device cases
 * instantly, so the polling interval is conservative to spare quota.
 */
export const EMAIL_VERIFICATION_POLL_MS = 15_000;

/**
 * Resend cooldowns — Supabase enforces server-side rate limits on resend
 * (~60 s); the UI mirrors the limit so users don't see opaque 429
 * responses. Same value for email verification resend and password reset
 * request resend so the experience is consistent.
 */
export const AUTH_RESEND_COOLDOWN_SECONDS = 60;

/**
 * Password reset — when the user lands on `/auth/reset-password` from the
 * email link, Supabase needs a moment to parse the URL fragment and
 * establish the recovery session. We wait `PASSWORD_RECOVERY_TIMEOUT_MS`
 * for either a `PASSWORD_RECOVERY` event or a non-null
 * `getSession()` response. After that, we assume the link is dead and
 * bounce the user back to step 1.
 */
export const PASSWORD_RECOVERY_TIMEOUT_MS = 3_000;

// Note: an earlier draft exported `PASSWORD_RESET_AUTO_REDIRECT_MS = 0`
// for a 1.5 s post-reset auto-redirect. The UI now uses an explicit
// "Continue" button (see PasswordResetConfirmPage); the constant was
// removed after verifying no callers remained.
